import { createEffect, type JSX, onCleanup, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv } from '../lib/tv.ts'
import { SparkIcon } from './icons.tsx'
import { Sheet } from './sheet.tsx'

const ASSISTANT_PANEL_ID = 'veem-assistant-panel'

const assistantLauncher = tv({
  base: [
    'fixed right-4 bottom-4 z-40 flex h-12 min-w-12 items-center justify-center gap-2 rounded-pill',
    'bg-ink px-3.5 text-ui font-medium text-text-inverse shadow-overlay',
    'transition-[background-color,opacity,transform,visibility]',
    'duration-(--duration-press) ease-out',
    'hover:bg-ink-hover active:scale-[0.96]',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  ],
})

export interface AssistantPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  subtitle?: string
  /** Conversation + composer are composed by the caller. */
  children: JSX.Element
}

export interface AssistantLauncherProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Small count on the launcher, e.g. unread proposals. Omit when zero. */
  badge?: string
}

export const AssistantPanel = (props: AssistantPanelProps) => {
  const [local] = splitProps(props, ['open', 'onOpenChange', 'title', 'subtitle', 'children'])

  return (
    <Sheet
      id={ASSISTANT_PANEL_ID}
      data-assistant-panel
      open={local.open}
      onOpenChange={local.onOpenChange}
      title={local.title ?? 'АМ-Автопром · помічник'}
      description={
        <span class="flex items-center gap-1.5 text-micro">
          <span class="size-1.5 rounded-pill bg-status-done" aria-hidden="true" />
          {local.subtitle ?? 'Готовий працювати з контекстом'}
        </span>
      }
      closeLabel="Закрити помічника"
      icon={<SparkIcon />}
      initialFocus="[data-assistant-autofocus]"
      scrimStyle="companion"
      restoreFocus={false}
      bodyClass="flex min-h-0 flex-1 flex-col p-0"
      class="bottom-[4.75rem] h-[min(34rem,calc(100dvh-9rem))] md:top-auto md:bottom-4 md:h-[min(38rem,calc(100dvh-2rem))] md:w-[min(26rem,calc(100vw-2rem))]"
    >
      {local.children}
    </Sheet>
  )
}

export const AssistantLauncher = (props: AssistantLauncherProps) => {
  const [local] = splitProps(props, ['open', 'onOpenChange', 'badge'])
  let launcher!: HTMLButtonElement
  let wasOpen = false

  createEffect(() => {
    const isOpen = local.open
    if (wasOpen && !isOpen) {
      const restoreFocus = () => launcher.focus()
      launcher.addEventListener('transitionend', restoreFocus, { once: true })
      const frame = requestAnimationFrame(restoreFocus)
      onCleanup(() => {
        cancelAnimationFrame(frame)
        launcher.removeEventListener('transitionend', restoreFocus)
      })
    }
    wasOpen = isOpen
  })

  return (
    <button
      ref={launcher}
      type="button"
      data-assistant-launcher
      class={cn(assistantLauncher(), local.open && 'pointer-events-none invisible opacity-0')}
      aria-label="Відкрити помічника"
      aria-expanded={local.open}
      aria-controls={ASSISTANT_PANEL_ID}
      tabIndex={local.open ? -1 : 0}
      onClick={() => local.onOpenChange(true)}
    >
      <SparkIcon />
      <span>Помічник</span>
      {local.badge ? (
        <span class="absolute -top-1 -right-1 min-w-4 rounded-pill bg-status-critical px-1 text-center text-micro font-semibold text-text-inverse">
          {local.badge}
        </span>
      ) : null}
    </button>
  )
}
